import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { Download } from 'lucide-react'
import { useMemo, useState } from 'react'
import { trpc, trpcClient, type RouterOutputs } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { saveRegisterFile, type Base64File } from 'shared/components/partner-registers/register-file'
import { SectionTitle } from 'shared/components/section'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { useDataTable } from 'shared/hooks/use-data-table'
import { useTranslate } from 'shared/lib/i18n'
import { formatDayTime } from 'shared/lib/utils/date'

type Inventory = RouterOutputs['inventories']['List'][number]

/**
 * «Инвентаризации» (ADR-0045, ticket 54): the Points' inventories as one table, each
 * one's result as Excel — taken, not found, extras — one click away. 139 Express has
 * no webhook, so support sends it the file itself. The extras are Discrepancies, in
 * «Расхождения». The file is the server's; the calls are injected.
 */
export function Inventories({
	result = (inventoryId: string) => trpcClient.inventories.Result.query({ inventoryId }),
	saveFile = saveRegisterFile,
}: {
	result?: (inventoryId: string) => Promise<Base64File>
	saveFile?: (file: Base64File) => void
}) {
	const { t } = useTranslate()
	const query = useQuery(trpc.inventories.List.queryOptions())
	const items = useMemo(() => query.data ?? [], [query.data])
	const [error, setError] = useState<string | null>(null)

	const columns = useMemo<ColumnDef<Inventory>[]>(() => {
		const download = async (inventoryId: string) => {
			setError(null)
			try {
				saveFile(await result(inventoryId))
			} catch (e) {
				setError(t('inventory.failed', { message: e instanceof Error ? e.message : String(e) }))
			}
		}
		return [
			{ id: 'point', header: t('inventory.supportColPoint'), cell: ({ row: { original: i } }) => i.pointCode },
			{ id: 'partner', header: t('inventory.supportColPartner'), cell: ({ row: { original: i } }) => i.partnerName },
			{
				id: 'state',
				header: t('inventory.supportColState'),
				cell: ({ row: { original: i } }) => (
					<Badge variant={i.state === 'OPEN' ? 'default' : 'secondary'}>{t(`inventory.state_${i.state}`)}</Badge>
				),
			},
			{
				id: 'taken',
				header: t('inventory.supportColTaken'),
				meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' },
				cell: ({ row: { original: i } }) =>
					t('inventory.supportTakenOf', { taken: String(i.counts.taken), total: String(i.counts.total) }),
			},
			{
				id: 'opened',
				header: t('inventory.supportColOpened'),
				cell: ({ row: { original: i } }) => (
					<span className='text-muted-foreground tabular-nums'>{formatDayTime(i.openedAt)}</span>
				),
			},
			{
				id: 'actions',
				header: '',
				meta: { cellClassName: 'w-px text-right' },
				cell: ({ row: { original: i } }) => (
					<Button size='sm' variant='outline' onClick={() => download(i.inventoryId)} data-testid='support-inventory-download'>
						<Download />
						{t('inventory.download')}
					</Button>
				),
			},
		]
	}, [t, result, saveFile])

	const { table } = useDataTable({
		data: items,
		columns,
		rowCount: items.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		autoResetPageIndex: false,
		getRowId: (i) => i.inventoryId,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})

	return (
		<section className='flex flex-col gap-2'>
			<SectionTitle title={t('inventory.supportTitle')} count={query.data?.length} hint={t('inventory.supportHint')} />
			{error && (
				<p role='alert' className='text-sm text-destructive'>
					{error}
				</p>
			)}
			{query.isError ? (
				<p className='text-sm text-destructive'>{query.error.message}</p>
			) : query.isLoading ? (
				<p className='text-sm text-muted-foreground'>{t('inventory.loading')}</p>
			) : (
				<DataTable
					table={table}
					withPagination={false}
					emptyText={<span data-testid='support-inventories-empty'>{t('inventory.supportEmpty')}</span>}
					row={{ attributes: () => ({ 'data-testid': 'support-inventory' }) }}
				/>
			)}
		</section>
	)
}
